import { useState, useEffect } from 'react'
import { VITE_API_BASE as API_BASE } from '../config/apiConfig'
import { Table, Button, Modal, message, Input, Select, Space, Tag, Card, Row, Col, Statistic, Popconfirm, Alert } from 'antd'
import { PlusOutlined, EditOutlined, CloudUploadOutlined, DeleteOutlined, EyeOutlined, CheckOutlined, SendOutlined, UploadOutlined } from '@ant-design/icons'

const { TextArea } = Input
const API = API_BASE

const CONTENT_TYPES = [
  { value: '品牌公告', label: '📌 品牌公告' },
  { value: '活动预告', label: '🎉 活动预告' },
  { value: '育儿知识', label: '📚 育儿知识' },
  { value: '课程介绍', label: '🎓 课程介绍' },
  { value: '招生推广', label: '📢 招生推广' },
]

const PLATFORM_OPTIONS = [
  { value: 'douyin', label: '🎵', name: '抖音', color: '#1a1a1a', bg: '#f5f5f5' },
  { value: 'xiaohongshu', label: '📕', name: '小红书', color: '#ff2442', bg: '#fff0f0' },
  { value: 'wechannel', label: '📺', name: '视频号', color: '#07c160', bg: '#f0fff4' },
]

const STATUS_MAP = {
  published: { color: 'green', text: '已发布' },
  draft: { color: 'default', text: '草稿' },
  pending: { color: 'orange', text: '待发布' },
  failed: { color: 'red', text: '失败' },
}

const getStatusTag = (s) => {
  const m = STATUS_MAP[s] || { color: 'default', text: s }
  return <Tag color={m.color}>{m.text}</Tag>
}

const getPlatformLabel = (p) => PLATFORM_OPTIONS.find(x => x.value === p)?.name || p
const getPlatformIcon = (p) => PLATFORM_OPTIONS.find(x => x.value === p)?.label || '🌐'
const getPlatformMeta = (p) => PLATFORM_OPTIONS.find(x => x.value === p) || { label: '🌐', name: p, color: '#888', bg: '#f5f5f5' }

export default function HQContentPage() {
  const [contents, setContents] = useState([])
  const [loading, setLoading] = useState(false)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [typeFilter, setTypeFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [modal, setModal] = useState({ open: false, mode: 'create', data: null })
  const [form, setForm] = useState({ title: '', type: '品牌公告', content: '', summary: '', cover_image: '', author: '', distribution_scope: 'all' })
  const [crossModal, setCrossModal] = useState({ open: false, content: null })
  const [crossPosts, setCrossPosts] = useState([])
  const [crossLoading, setCrossLoading] = useState(false)

  const pageSize = 20

  useEffect(() => { loadContents() }, [page, typeFilter, statusFilter])

  const loadContents = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({ page, pageSize })
      if (typeFilter) params.set('type', typeFilter)
      if (statusFilter) params.set('status', statusFilter)
      const res = await fetch(`${API}/hq-content?${params}`)
      const json = await res.json()
      if (json.success) {
        setContents(json.data || [])
        setTotal(json.total || 0)
      } else {
        message.error(json.error || '加载失败')
      }
    } catch (e) {
      message.error('网络错误，加载失败')
    }
    setLoading(false)
  }

  const openCreate = () => {
    setForm({ title: '', type: '品牌公告', content: '', summary: '', cover_image: '', author: '博策教育总部', distribution_scope: 'all' })
    setModal({ open: true, mode: 'create', data: null })
  }

  const openEdit = (c) => {
    setForm({
      title: c.title || '',
      type: c.type || '品牌公告',
      content: c.content || '',
      summary: c.summary || '',
      cover_image: c.cover_image || '',
      author: c.author || '',
      distribution_scope: c.distribution_scope || 'all'
    })
    setModal({ open: true, mode: 'edit', data: c })
  }

  const handleSave = async () => {
    if (!form.title.trim()) return message.error('请输入标题')
    setLoading(true)
    try {
      const url = modal.mode === 'edit' ? `${API}/hq-content/${modal.data.id}` : `${API}/hq-content`
      const method = modal.mode === 'edit' ? 'PUT' : 'POST'
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      })
      const json = await res.json()
      if (json.success) {
        message.success(modal.mode === 'edit' ? '更新成功' : '创建成功')
        setModal({ open: false, data: null })
        loadContents()
      } else {
        message.error(json.error || '保存失败')
      }
    } catch (e) {
      message.error('网络错误')
    }
    setLoading(false)
  }

  const handlePublish = async (id) => {
    try {
      const res = await fetch(`${API}/hq-content/${id}/publish`, { method: 'PUT' })
      const json = await res.json()
      if (json.success) { message.success('发布成功'); loadContents() }
      else message.error(json.error || '发布失败')
    } catch (e) { message.error('网络错误') }
  }

  const handleUnpublish = async (id) => {
    try {
      const res = await fetch(`${API}/hq-content/${id}/unpublish`, { method: 'PUT' })
      const json = await res.json()
      if (json.success) { message.success('已下架'); loadContents() }
      else message.error(json.error || '下架失败')
    } catch (e) { message.error('网络错误') }
  }

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${API}/hq-content/${id}`, { method: 'DELETE' })
      const json = await res.json()
      if (json.success) { message.success('已删除'); loadContents() }
      else message.error(json.error || '删除失败')
    } catch (e) { message.error('网络错误') }
  }

  const openCrossPost = async (content) => {
    setCrossModal({ open: true, content })
    setCrossPosts([])
    try {
      const res = await fetch(`${API}/hq-content/${content.id}/cross-posts`)
      const json = await res.json()
      if (json.success) setCrossPosts(json.data || [])
    } catch (e) { /* ignore */ }
  }

  const handleCrossPost = async (platforms) => {
    try {
      const res = await fetch(`${API}/hq-content/${crossModal.content.id}/cross-posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ platforms })
      })
      const json = await res.json()
      if (json.success) {
        message.success('已添加到发布队列')
        const r2 = await fetch(`${API}/hq-content/${crossModal.content.id}/cross-posts`)
        const j2 = await r2.json()
        if (j2.success) setCrossPosts(j2.data || [])
      } else {
        message.error(json.error || '创建失败')
      }
    } catch (e) { message.error('网络错误') }
  }

  const handleCrossPublish = async (postId, platform, platformName) => {
    setCrossLoading(true)
    try {
      const res = await fetch(`${API}/hq-content/posts/${postId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'published',
          platform_url: `https://${platform}.com/simulated/${postId}`
        })
      })
      const json = await res.json()
      if (json.success) {
        message.success(`${platformName} 发布成功（模拟模式）`)
        const r2 = await fetch(`${API}/hq-content/${crossModal.content.id}/cross-posts`)
        const j2 = await r2.json()
        if (j2.success) setCrossPosts(j2.data || [])
      } else {
        message.error(json.error || '发布失败')
      }
    } catch (e) { message.error('网络错误') }
    setCrossLoading(false)
  }

  const columns = [
    {
      title: '内容',
      key: 'content',
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{r.title}</div>
          <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
            <Tag>{r.type}</Tag>
            {r.distribution_scope === 'all'
              ? <Tag color="blue">🌐 全部分发</Tag>
              : <Tag>🏠 本机构</Tag>
            }
            <span style={{ marginLeft: 8 }}>👁 {r.view_count || 0}</span>
            <span style={{ marginLeft: 8 }}>❤️ {r.like_count || 0}</span>
          </div>
          {r.summary && (
            <div style={{
              fontSize: 12, color: '#666', marginTop: 4,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              maxWidth: 400
            }}>
              {r.summary}
            </div>
          )}
        </div>
      )
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: s => getStatusTag(s)
    },
    {
      title: '作者',
      dataIndex: 'author',
      width: 120,
      render: a => a || '-'
    },
    {
      title: '发布时间',
      dataIndex: 'published_at',
      width: 160,
      render: t => t ? new Date(t).toLocaleString('zh-CN') : '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 300,
      render: (_, r) => (
        <Space size={4} wrap>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>编辑</Button>
          {r.status === 'draft' && (
            <Button size="small" type="primary" icon={<UploadOutlined />} onClick={() => handlePublish(r.id)}>
              发布
            </Button>
          )}
          {r.status === 'published' && (
            <Button size="small" danger icon={<CloudUploadOutlined />} onClick={() => handleUnpublish(r.id)}>
              下架
            </Button>
          )}
          <Button size="small" icon={<CheckOutlined />} onClick={() => openCrossPost(r)}>跨平台发布</Button>
          <Popconfirm title="确定删除？" onConfirm={() => handleDelete(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  const draftCount = contents.filter(c => c.status === 'draft').length
  const publishedCount = contents.filter(c => c.status === 'published').length

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0 }}>🏢 总部内容中心</h2>
          <p style={{ margin: '4px 0 0', color: '#888', fontSize: 13 }}>
            管理官方品牌内容，创建后可一键分发至各机构，也可发布到抖音/小红书/视频号
          </p>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} size="large">
          创建内容
        </Button>
      </div>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={8}><Statistic title="全部内容" value={total} /></Col>
        <Col span={8}><Statistic title="已发布" value={publishedCount} valueStyle={{ color: '#52c41a' }} /></Col>
        <Col span={8}><Statistic title="草稿" value={draftCount} valueStyle={{ color: '#fa8c16' }} /></Col>
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            allowClear placeholder="内容类型" style={{ width: 150 }}
            value={typeFilter || undefined}
            onChange={v => { setTypeFilter(v || ''); setPage(1) }}
            options={[{ value: '', label: '全部类型' }, ...CONTENT_TYPES]}
          />
          <Select
            allowClear placeholder="状态" style={{ width: 120 }}
            value={statusFilter || undefined}
            onChange={v => { setStatusFilter(v || ''); setPage(1) }}
            options={[
              { value: '', label: '全部状态' },
              { value: 'published', label: '已发布' },
              { value: 'draft', label: '草稿' },
            ]}
          />
        </Space>
      </Card>

      <Card>
        <Table
          dataSource={contents}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            pageSize,
            total,
            onChange: p => setPage(p),
            showTotal: t => `共 ${t} 条`,
            showSizeChanger: false,
          }}
        />
      </Card>

      {/* 创建/编辑 Modal */}
      <Modal
        open={modal.open}
        title={modal.mode === 'create' ? '📝 创建内容' : '✏️ 编辑内容'}
        onCancel={() => setModal({ open: false, data: null })}
        footer={
          <Space>
            <Button onClick={() => setModal({ open: false, data: null })}>取消</Button>
            <Button type="primary" icon={<CheckOutlined />} onClick={handleSave} loading={loading}>
              保存
            </Button>
          </Space>
        }
        width={720}
      >
        <div style={{ display: 'grid', gap: 16, paddingTop: 8 }}>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>标题 <span style={{ color: 'red' }}>*</span></label>
            <Input
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="输入内容标题"
              maxLength={100}
              showCount
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>内容类型</label>
              <Select
                value={form.type}
                onChange={v => setForm(f => ({ ...f, type: v }))}
                options={CONTENT_TYPES}
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>分发范围</label>
              <Select
                value={form.distribution_scope}
                onChange={v => setForm(f => ({ ...f, distribution_scope: v }))}
                style={{ width: '100%' }}
                options={[
                  { value: 'all', label: '🌐 分发至所有机构' },
                  { value: 'single', label: '🏠 分发至指定机构' },
                ]}
              />
            </div>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>摘要</label>
            <Input
              value={form.summary}
              onChange={e => setForm(f => ({ ...f, summary: e.target.value }))}
              placeholder="简要描述，用于列表展示（选填）"
              maxLength={200}
              showCount
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>正文内容</label>
            <TextArea
              value={form.content}
              onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
              rows={8}
              placeholder="输入完整的正文内容，支持多段落..."
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>作者/来源</label>
              <Input
                value={form.author}
                onChange={e => setForm(f => ({ ...f, author: e.target.value }))}
                placeholder="博策教育总部"
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>封面图片URL</label>
              <Input
                value={form.cover_image}
                onChange={e => setForm(f => ({ ...f, cover_image: e.target.value }))}
                placeholder="https://api.shitonghui.cn/images/xxx.jpg"
              />
            </div>
          </div>
        </div>
      </Modal>

      {/* 跨平台发布 Modal */}
      <Modal
        open={crossModal.open}
        title={`🚀 跨平台发布 - ${crossModal.content?.title}`}
        onCancel={() => setCrossModal({ open: false, content: null })}
        footer={null}
        width={680}
      >
        {/* 模拟模式提示 */}
        <Alert
          message="⚠️ 当前为模拟发布模式"
          description="各平台 API 权限需要单独申请。当前点击「发布」会生成模拟链接，用于演示和内容规划。正式上线需对接各平台开放平台接口。"
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <div style={{ marginBottom: 20 }}>
          <h4 style={{ marginBottom: 12 }}>📱 选择发布平台：</h4>
          <Space size={12} wrap>
            {PLATFORM_OPTIONS.map(p => {
              const alreadyPosted = crossPosts.some(cp => cp.platform === p.value)
              return (
                <div key={p.value} style={{ textAlign: 'center' }}>
                  <Button
                    onClick={() => !alreadyPosted && handleCrossPost([p.value])}
                    disabled={alreadyPosted}
                    style={{
                      height: 72, width: 110,
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center',
                      background: alreadyPosted ? '#f5f5f5' : p.bg,
                      border: alreadyPosted ? '1px solid #d9d9d9' : `1px solid ${p.color}40`,
                    }}
                  >
                    <span style={{ fontSize: 28 }}>{p.label}</span>
                    <span style={{ fontSize: 13, color: alreadyPosted ? '#999' : p.color, fontWeight: 500 }}>
                      {p.name}
                    </span>
                  </Button>
                  {alreadyPosted && (
                    <div style={{ fontSize: 11, color: '#52c41a', marginTop: 2 }}>
                      ✓ 已添加
                    </div>
                  )}
                </div>
              )
            })}
          </Space>
        </div>

        {/* 发布记录 */}
        {crossPosts.length > 0 && (
          <div>
            <h4 style={{ marginBottom: 10 }}>📋 发布记录：</h4>
            <Table
              size="small"
              dataSource={crossPosts}
              rowKey="id"
              pagination={false}
              loading={crossLoading}
              columns={[
                {
                  title: '平台',
                  dataIndex: 'platform',
                  width: 120,
                  render: p => {
                    const meta = getPlatformMeta(p)
                    return (
                      <Space>
                        <span style={{ fontSize: 16 }}>{meta.label}</span>
                        <span>{meta.name}</span>
                      </Space>
                    )
                  }
                },
                {
                  title: '状态',
                  dataIndex: 'status',
                  width: 100,
                  render: s => getStatusTag(s)
                },
                {
                  title: '时间',
                  dataIndex: 'published_at',
                  width: 160,
                  render: t => t ? new Date(t).toLocaleString('zh-CN') : '-'
                },
                {
                  title: '操作',
                  width: 140,
                  render: (_, r) => {
                    const meta = getPlatformMeta(r.platform)
                    if (r.status === 'published') {
                      return (
                        <a href={r.platform_url} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>
                          🔗 查看链接
                        </a>
                      )
                    }
                    if (r.status === 'failed') {
                      return <span style={{ color: '#ff4d4f', fontSize: 12 }}>❌ 发布失败</span>
                    }
                    return (
                      <Button
                        size="small"
                        type="primary"
                        icon={<SendOutlined />}
                        onClick={() => handleCrossPublish(r.id, r.platform, meta.name)}
                        loading={crossLoading}
                      >
                        发布
                      </Button>
                    )
                  }
                }
              ]}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
              💡 提示：点击「发布」后将状态更新为已发布，并生成模拟链接。真实发布需各平台授权。
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
